import {Box, CircularProgress, debounce, InputAdornment, MenuItem, Select} from '@mui/material';
import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {Info, Plus} from 'react-feather';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router';
import {CustomTooltip} from '../../../../styles/components/CustomTooltip';
import {errorBigIcon, iconNotifyWarningInfo, toastSuccess,} from '../../../../assets/icons/icons';
import {Button, Typography, WhiteCard} from '../../../../components/StyledComponents';
import {
  EGithubUserTypes,
  EPaymentRequiredType,
  EPlatformsNames,
  ESubscriptionPlans,
  ETrialStatus,
  SubscriptionMockData
} from '../../../../models/consts';
import {IError, IPaymentRequiredPoppup, IPoppupBase, PlatformProps} from '../../../../models/inner-models';
import Platform from '../../../../models/platforms-data';
import {
  backupGistAll,
  backupRepoAll,
  deleteGithubGist,
  deleteGithubItem,
  fetchGistForGithubUser,
  fetchGithubUsers,
  fetchItemsForGithubUser,
  getAllSubscriptions,
} from '../../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {
  getGithubAppInstallSelector,
  getGithubCanUserBackup,
  getGithubReposSelector,
  getGithubTotal,
  getGithubUsersSelector,
  getIsTestUser,
  githubSubscription,
  githubTrialHoursLeft,
  githubTrialPlanQuantity,
  githubTrialQuantityLeft,
  githubTrialStatus,
  isGithubRepoLoadingSelector,
  isGithubUsersLoadingSelector,
  isSubUser,
  loadingUser,
  roleByApp,
} from '../../../../store/selectors';
import {SearchInput} from '../../../../styles/components/MainInputElements';
import getSubInfo from '../../../../utils/platformSubscriptionInfo';
import withRedirectAfterLogout from '../../../../utils/withRedirectAfterLogout';
import '../../style.scss';
import GithubOrgAccInfoDialog from "./GithubOrgAccInfoDialog/GithubOrgAccInfoDialog";
import fetchItemsErrorHandler from "../../../../utils/fetchItemsErrorHandler";
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import PlatformItemsList from "../../../../components/PlatformComponents/PlatformItemsList/PlatformItemsList";
import GithubRepoAdd from "./GithubRepoAddDialog/GithubRepoAdd";
import GithubGistAdd from "./GithubRepoAddDialog/GithubGistAdd";
import PaymentRequiredPoppup from "../../../../components/Dialogs/PaymentRequiredPoppup/PaymentRequiredPoppup";
import {defaultTrialReposMax, EWebsocketMethods} from "../../../../constants";
import NoSubscriptionBlock from "../../../../components/Blocks/NoSubscriptionBlock/NoSubscriptionBlock";
import NoBackupsBlock from "../../../../components/Blocks/NoItemsBlock/NoItemsBlock";
import UserNotConfirmedBlock from "../../../../components/Blocks/UserNotConfirmedBlock/UserNotConfirmedBlock";
import Modal from "../../../../components/Dialogs/Modal/Modal";
import GithubUserAddEdit from "../GithubUserAddEditView/GithubUserAddEdit";
import DeleteItemDialog from "../../../../components/Dialogs/DeleteItemDialog/DeleteItemDialog";
import {IDeletePayload, IPlatformUser} from "../../../../models/models";
import {
  BackupBadges,
  BadgesAddingItems,
  BadgesAppWasDeleted,
  BadgesIFrameMode,
  BadgesSubscription,
  BadgesTrial2
} from "../../../../components/Badges/Badges";
import GithubAddOrg from "./GithubAddOrg/GithubAddOrg";
import CustomTimerTooltip from "../../../../styles/components/CustomTimerTooltip";
import {updateSubscriptionPlanModal} from "../../../../utils/functions/updateSubscriptionPlanModal";
import ExternalStorage from "../../../UserPages/UserSettings/ExternalStorage/ExternalStorage";
import {useWebSocket} from "../../../../contexts/WebSocketContext";
import {iniFrame} from "../../../../serviceWorker";
import {AddSquare, Play, SearchNormal} from "iconsax-reactjs";
import ExtStoreIco from "../../../../assets/icons/CustomSvg/ExtStoreIco";

const githubOrgInfoSeenKey = (orgId: number) => `github_org_info_seen_${orgId}`;

const hasSeenGithubOrgInfo = (orgId: number) => localStorage.getItem(githubOrgInfoSeenKey(orgId)) === '1';

const markGithubOrgInfoSeen = (orgId: number) => {
  localStorage.setItem(githubOrgInfoSeenKey(orgId), '1');
};

const GithubPlatformSettings: FunctionComponent = () => {
  const {t: translate} = useTranslation();
  const userLoading = useAppSelector(loadingUser);
  const isLoadingRepos = useAppSelector(isGithubRepoLoadingSelector);
  const isLoadingGithubUsers = useAppSelector(isGithubUsersLoadingSelector);
  const trialState = useAppSelector(githubTrialStatus);
  const repos = useAppSelector(getGithubReposSelector);
  const total = useAppSelector(getGithubTotal);
  const canUserBackup = useAppSelector(getGithubCanUserBackup);
  const isTestUser = useAppSelector(getIsTestUser);
  const trialDays = useAppSelector(githubTrialHoursLeft);
  const trialQuantity = useAppSelector(githubTrialQuantityLeft);
  const trialPlanQuantity = useAppSelector(githubTrialPlanQuantity);
  const subInfo = useAppSelector(githubSubscription);
  const users = useAppSelector(getGithubUsersSelector);
  const hasUserApp = useAppSelector(getGithubAppInstallSelector);
  const isUserSub = useAppSelector(isSubUser);
  const roleApp = useAppSelector(roleByApp);
  const isAdminRole = isUserSub && roleApp?.github === 'admin' && (trialState === 'active' || subInfo?.planName === 'Pro') || !isUserSub
  // disable btn sub not admin - isUserSub && roleApp?.github === 'user'
  // const isValidToken = useAppSelector(getGithubTokenValidSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const platformName = EPlatformsNames.GITHUB;
  const platform = Platform[platformName];
  const [searchParams] = useSearchParams();
  const [isAppJustAdded, setIsAppJustAdded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [platformInfo, setPlatformInfo] = useState<PlatformProps>(
    {
      platformName: platformName,
      subPlan: SubscriptionMockData[ESubscriptionPlans.NO_SUB],
      items: repos || [],
      filteredItems: repos || [],
      users: users || [],
    },
  );

  const [notification, setNotification] = useState<IPoppupBase>({isOpen: false,});
  const [notificationSec, setNotificationSec] = useState<IPoppupBase>({isOpen: false,});
  const [notificationPlace, setNotificationPlace] = useState<IPoppupBase>({isOpen: false});
  const [paymentPoppup, setPaymentPoppup] = useState<IPaymentRequiredPoppup>({
    isOpen: false,
    platformName,
  });
  const [paymentState, setPaymentState] = useState<EPaymentRequiredType>(EPaymentRequiredType.NON);
  const [buyShowed, setBuyShowed] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showSettings2, setShowSettings2] = useState<boolean>(false);
  const [deleteDate, setDeleteData] = useState<{ id?: string, name?: string, type?: string }>({});
  const [deleteBulkDate, setDeleteBulkData] = useState<Array<string>>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectList, setSelectList] = useState<Array<string>>([]);
  const [progressItem, setProgressItem] = useState<string>('');
  const appWasDeleted = !!users && (!hasUserApp || !users.filter(i => i.githubapp).length) && platformInfo?.items?.length !== 0 //&& !users.filter(i => i.isConfirmed).length
  const [progressEnd, setProgressEnd] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [intervalFunc, setIntervalFunc] = useState<any>();
  const [userId, setUserId] = useState<number>(1);
  const [userCanBackup, setUserCanBackup] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [addingItemsData, setAddingItemsData] = useState<{ addedItems: number; totalItems: number }>({
    addedItems: 0,
    totalItems: 0
  });
  const {onMessage} = useWebSocket();
  const [filterValue, setFilterValue] = useState<string>('');
  const [autoAddRepo, setAutoAddRepo] = useState<boolean>(false);
  const [pendingAddReposAfterOrgInfo, setPendingAddReposAfterOrgInfo] = useState(false);

  const stripGithubOnboardingParams = useCallback(() => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    if (!updatedSearchParams.has('isAppJustAdded') && !updatedSearchParams.has('openAddModal')) {
      return;
    }
    updatedSearchParams.delete('isAppJustAdded');
    updatedSearchParams.delete('openAddModal');
    navigate({search: updatedSearchParams.toString()}, {replace: true});
  }, [navigate, searchParams]);

  useEffect(() => {
    const isExt = searchParams.get('isCloudStorageConnected');
    const errorMessage = searchParams.get('error_message');

    if (!errorMessage && isExt) {
      setShowSettings2(true)
    }
  })

  useEffect(() => {
    if (!isLoadingRepos && !repos?.length && !!users?.length && !isUserSub && !autoAddRepo && !filterValue && !searchTerm) {
      setAutoAddRepo(true)
    }
  }, [isLoadingRepos, repos, users, isUserSub, autoAddRepo, filterValue, searchTerm]);

  const clearState = (notPage?: boolean) => {
    setDeleteData({})
    setDeleteBulkData([])
    setSelectList([])
    setSelectAll(false)
    if (!notPage) {
      setPage(0)
    }
  }

  useEffect(() => {
    setUserCanBackup(canUserBackup)
  }, [canUserBackup])

  const restartBadge = () => {
    if (intervalFunc) {
      clearInterval(intervalFunc)
      setIntervalFunc(null)
    }
    setProgressEnd(false)
    setProgressItem('')
  }

  const setUserPlace = (userList: IPlatformUser[] | null) => {
    if (!!userList?.length && userList[0].id && (userId === 1 || !localStorage['githubUser'] || Number(localStorage['githubUser']) !== userId || localStorage['githubUser'] === '111')) {
      if (Number(localStorage['githubUser']) === 0) {
        setUserId(0)
      } else if (localStorage['githubUser'] && !!userList?.filter(i => i.id === Number(localStorage['githubUser']))?.length) {
        setUserId(Number(localStorage['githubUser']))
        return localStorage['githubUser'] as number
      } else {
        localStorage['githubUser'] = userList[0].id
        setUserId(userList[0].id)
        return userList[0].id
      }
    }
  }

  useEffect(() => {
    setTimeout(() => setUserPlace(users), 2000)
  }, []);

  const fetchData = async (newPage?: number, name?: string, filter?: string) => {
    try {
      setIsLoading(true)
      restartBadge()
      let usersList: { result: IPlatformUser[] | null } = {result: null}
      if (!users) {
        usersList = await dispatch(fetchGithubUsers()).unwrap().catch(err => console.log(err));
      }

      Number(localStorage['githubUser']) === 0 ?
        await dispatch(fetchGistForGithubUser({
          page: newPage || 0,
          perPage: 10,
          name,
          filter: filter !== undefined ? filter : filterValue
        })).unwrap()
          .catch((err) => {
            console.log(err)
            setIsLoading(false)
            const error = err as IError;
            fetchItemsErrorHandler(error)
          })
          .finally(() => {
            setIsLoading(false)
          }) :
        await dispatch(fetchItemsForGithubUser({
          page: newPage || 0,
          perPage: 10,
          placeId: users && userId !== 1 ? userId : setUserPlace(!!usersList?.result ? usersList.result : users) as number,
          name, filter: filter !== undefined ? filter : filterValue
        })).unwrap()
          .catch((err) => {
            console.log(err)
            setIsLoading(false)
            const error = err as IError;
            fetchItemsErrorHandler(error)
          })
          .finally(() => {
            setIsLoading(false)
          })

    } catch (e) {
      console.log(e);
      setIsLoading(false)
    }
  };

  useEffect(() => {
    if (platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) {
      navigate('/app-connector')
    }
    fetchData();

    onMessage((data) => {
      if (EWebsocketMethods.itemsAddedCountChange === data.method && data.platform === EPlatformsNames.GITHUB) {
        setAddingItemsData({addedItems: data.addedItems, totalItems: data.totalItems})
        if (data.addedItems === data.totalItems) {
          fetchData()
        }
      }
    });
  }, []);

  useEffect(() => {
    if ((platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && !platformInfo.subPlan.periodHours) ||
      (platformInfo.subPlan.type === ESubscriptionPlans.SUBSCRIPTION && !platformInfo.subPlan.isPaid)) {
      setPaymentState(
        platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && !platformInfo.subPlan.periodHours ?
          EPaymentRequiredType.TRIAL : platformInfo.subPlan.isPaid ?
            EPaymentRequiredType.NON : EPaymentRequiredType.SUB,
      );
    } else {
      setPaymentState(EPaymentRequiredType.NON)
    }
  }, [platformInfo]);

  useEffect(() => {
    if (isUserSub && !roleApp?.[platformName]) {
      navigate('/', {replace: true})
    }
  }, [isUserSub, roleApp]);

  useEffect(() => {
    if (!isUserSub && !isLoadingRepos && trialState === 'expired' && (!subInfo || subInfo?.status !== 'active') && !buyShowed) {
      setBuyShowed(true)
      Swal.fire({
        title: !!subInfo ? translate('notifications.subscription.subscribe_buy') : translate('notifications.subscription.subscribe_buy_trial'),
        text: !!subInfo ? translate('notifications.subscription.subscribe_buy_text') : translate('notifications.subscription.subscribe_buy_text_trial'),
        cancelButtonText: translate('notifications.choices.close'),
        confirmButtonText: translate('notifications.choices.buy_subscription_now'),
        showCancelButton: true,
        imageUrl: errorBigIcon,
      })
        .then((res) => {
          if (res.isConfirmed) {
            localStorage.setItem('checkout-platform', platformName)
            navigate('/checkout');
          }
        })
    }
  }, [isLoadingRepos, subInfo]);

  const addRepos = () => {
    if (paymentState !== EPaymentRequiredType.NON) {
      if (!isUserSub) {
        setPaymentPoppup((prev) => ({
          ...prev,
          isOpen: true,
        }));
      }
    } else {
      setNotification({
        isOpen: true,
      });
    }
  };

  const handleOrgInfoClose = (acknowledged?: boolean) => {
    const selected = users?.find((i) => i.id === userId);
    if (selected?.userType === EGithubUserTypes.ORGANIZATION) {
      markGithubOrgInfoSeen(selected.id);
    }
    setIsAppJustAdded(false);
    stripGithubOnboardingParams();
    if (acknowledged && pendingAddReposAfterOrgInfo) {
      setPendingAddReposAfterOrgInfo(false);
      addRepos();
    } else {
      setPendingAddReposAfterOrgInfo(false);
    }
  };

  useEffect(() => {
    if (!users?.length || searchParams.get('isAppJustAdded') !== 'true') {
      return;
    }
    const current = users.find((i) => i.id === userId);
    if (current?.userType === EGithubUserTypes.ORGANIZATION) {
      return;
    }
    const firstOrg = users.find((u) => u.userType === EGithubUserTypes.ORGANIZATION);
    if (!firstOrg) {
      stripGithubOnboardingParams();
      return;
    }
    localStorage['githubUser'] = String(firstOrg.id);
    setUserId(firstOrg.id);
    setIsLoading(true);
    dispatch(
      fetchItemsForGithubUser({
        page: 0,
        perPage: 10,
        placeId: firstOrg.id,
        filter: filterValue,
      }),
    )
      .unwrap()
      .finally(() => setIsLoading(false));
  }, [users, searchParams, userId, dispatch, filterValue, stripGithubOnboardingParams]);

  useEffect(() => {
    if (!users?.length || userId === 1 || userId === 0) {
      return;
    }
    if (isAppJustAdded) {
      return;
    }

    const selected = users.find((i) => i.id === userId);
    const isOrg = selected?.userType === EGithubUserTypes.ORGANIZATION;
    const openAddModalQuery = searchParams.get('openAddModal') === 'true';
    const isJustAddedQuery = searchParams.get('isAppJustAdded') === 'true';
    const wantsAddRepos =
      openAddModalQuery ||
      (autoAddRepo && !isLoadingRepos && !repos?.length && !filterValue && !searchTerm);

    if (isOrg && !hasSeenGithubOrgInfo(userId) && (isJustAddedQuery || openAddModalQuery || wantsAddRepos)) {
      setPendingAddReposAfterOrgInfo(true);
      setIsAppJustAdded(true);
      return;
    }

    if (wantsAddRepos) {
      stripGithubOnboardingParams();
      addRepos();
      return;
    }

    if (isJustAddedQuery && !isOrg) {
      stripGithubOnboardingParams();
    }
  }, [
    users,
    userId,
    searchParams,
    isLoadingRepos,
    repos,
    autoAddRepo,
    filterValue,
    searchTerm,
    isAppJustAdded,
    stripGithubOnboardingParams,
  ]);

  const addGist = () => {
    if (paymentState !== EPaymentRequiredType.NON) {
      if (!isUserSub) {
        setPaymentPoppup((prev) => ({
          ...prev,
          isOpen: true,
        }));
      }
    } else {
      setNotificationSec({
        isOpen: true,
      });
    }
  };

  const onConfirmDeleteRepo = async (id?, name?, isDelBulk?) => {
    try {
      // setIsLoading(true)
      const payload: IDeletePayload = {}
      let key = ''
      if (!!userId) {
        payload.placeId = userId
        key = 'reposIds'
      } else {
        key = 'gistsIds'
      }
      payload[key] = []
      if (!isDelBulk) {
        payload[key].push({id: id})
      } else {
        deleteBulkDate.forEach(item => {
          payload[key].push({id: item})
        })
      }
      let newPage = page
      if ((selectAll || platformInfo.items?.length === 1) && page > 0) {
        newPage = page - 1
        setPage(newPage)
      }
      if (!!userId) {
        await dispatch(deleteGithubItem(payload)).unwrap()
        await dispatch(fetchItemsForGithubUser({
          page: newPage,
          perPage: 10,
          placeId: userId,
          filter: filterValue
        })).unwrap()
      } else {
        await dispatch(deleteGithubGist(payload)).unwrap()
        await dispatch(fetchGistForGithubUser({page: newPage, perPage: 10, filter: filterValue})).unwrap()
      }
      dispatch(getAllSubscriptions()).unwrap()

      restartBadge()
      clearState(true)

      await Swal.fire({
        title: translate('notifications.titles.success'),
        text: isDelBulk ?
          translate('notifications.github_repos.successfully_deleted_many') :
          !!userId ?
            translate('notifications.github_repos.successfully_deleted') :
            translate('notifications.github_repos.successfully_deleted_gist'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
      // setIsLoading(false)

    } catch (err) {
      const error = err as IError;
      // setIsLoading(false)

      await Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  };

  const handleDeleteRepo = (id, name, type) => {
    setDeleteData({id, name, type});
  };

  const handleDeleteBulkBoard = async (arr) => {
    setDeleteBulkData(arr);
  };

  const closeNotification = useCallback(() => {
    setNotification({...notification, isOpen: false});
    setNotificationSec({...notification, isOpen: false});
    setNotificationPlace({...notification, isOpen: false});
  }, []);

  const closeTrialPoppup = useCallback(() => {
    setPaymentPoppup({...paymentPoppup, isOpen: false});
  }, []);

  const getRemainingItems = useCallback(() => {
    if (!platformInfo.subPlan) {
      return 0;
    }

    if (platformInfo.subPlan.quantity !== undefined && platformInfo.subPlan.quantity >= 0 && platformInfo.items) {
      const payedItemsLeft = platformInfo.subPlan.quantity;
      return payedItemsLeft > 0 ? payedItemsLeft : 0;
    }

    return defaultTrialReposMax;
  }, [platformInfo]);

  const validationBlock = useCallback(() => {
    if ((!!users?.filter(i => i.isConfirmed).length && platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
      (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED && !repos?.length)) {
      return <NoSubscriptionBlock platformName={platformName} isOwner={!isUserSub}/>;
    }
    if (!users?.length) {
      return <NoBackupsBlock
        isAdmin={!isUserSub}
        action={() => navigate('/app-connector')}
        platformItem={translate('views.bulk_restore.itemName.github.one')}
        actionText={translate('forms.github_add_edit_repo.configure_account')}
      />;
    }

    if ((!hasUserApp || !users.filter(i => i.githubapp).length) && platformInfo.items?.length === 0) {
      return <UserNotConfirmedBlock noApp={true} isOwner={!isUserSub}/>;
    }

    if (!!users?.length && (!users.filter(i => i.isConfirmed).length && !platformInfo.items?.length) || (!users.filter(i => i.isConfirmed).length && (hasUserApp || !!users.filter(i => i.githubapp).length) && !!platformInfo.items?.length)) {
      return <UserNotConfirmedBlock updUserList={setUserPlace} isOwner={!isUserSub}/>;
    }

    if (!platformInfo.items?.length) {
      return <NoBackupsBlock
        isSearch={!!searchTerm || !!filterValue}
        isAdmin={isAdminRole}
        action={!!userId ? addRepos : addGist}
        platformItem={translate('views.bulk_restore.itemName.github.one')}
        actionText={!!userId ? translate('forms.github_add_edit_repo.add_repo_num', {num: ''}) : translate('forms.github_add_edit_repo.add_gist_num', {num: ''})}
      />;
    }

    return null;
  }, [platformInfo, trialState, users, paymentState]);

  const backupAllNow = async () => {
    try {
      setIsLoading(true)
      if (!!userId) {
        await dispatch(backupRepoAll(userId)).unwrap();
      } else {
        await dispatch(backupGistAll()).unwrap();
      }
      restartBadge()
      if (total === 1) {
        setProgressItem(translate(`notifications.all.actionBadge.one.backing_up_process`, {
          app: 'GitHub',
          item: platformInfo.items?.[0]?.repo?.split('::')[0]
        }))
      } else {
        setProgressItem(translate(`notifications.all.actionBadge.many.backup`, {app: 'GitHub'}))
      }

      if (!isTestUser) {
        setUserCanBackup(false)
      }

      await Swal.fire({
        title: translate('notifications.titles.success'),
        text: total > 1 ? translate('notifications.github_repos.multi_start_backup') : translate('notifications.github_repos.single_start_backup'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
      setIsLoading(false)
    } catch (err) {
      const error = err as IError;
      setIsLoading(false)

      if (error.code === 'err.user.backup.limit') {
        setUserCanBackup(false)
        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          toast: true,
          position: 'top-end',
          timerProgressBar: true,
          showConfirmButton: false,
          showCloseButton: true,
          imageUrl: iconNotifyWarningInfo,
          timer: 3000,
        });
      } else {
        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          imageUrl: errorBigIcon,
          confirmButtonText: translate('notifications.choices.close'),
        });
      }
    }
  }


  useEffect(() => {
    if (!userLoading) {
      const subPlan = getSubInfo(trialState, subInfo, trialDays, platformName, trialQuantity, trialPlanQuantity);
      setPlatformInfo((prev) => ({
        ...prev,
        subPlan: {...subPlan},
      }));
    }
  }, [trialState, userLoading, trialDays, subInfo, trialQuantity, trialPlanQuantity]);

  useEffect(() => {
    if (!isLoadingRepos && !isLoadingGithubUsers) {
      setPlatformInfo((prev) => ({
        ...prev,
        items: repos || [],
        users: users || [],
        filteredItems: repos || [],
      }));
      setUserPlace(users)

      const filt = repos?.filter(itemB => !!itemB.actionProcess?.length || itemB.status === 'initialized')
      if (filt && filt.length > 1 && !progressItem.length) {
        const isRest = filt.filter(itemB => itemB.actionProcess === 'restoring_process')
        filt.length === isRest?.length ?
          setProgressItem(translate(`notifications.all.actionBadge.many.restore`, {app: 'GitHub'})) :
          !!isRest?.length ?
            setProgressItem(translate(`notifications.all.actionBadge.multi`)) :
            setProgressItem(translate(`notifications.all.actionBadge.many.backup`, {app: 'GitHub'}))
      } else if (filt?.length === 1 && !progressItem.length) {
        setProgressItem(translate(`notifications.all.actionBadge.one.${filt[0].actionProcess || 'backing_up_process'}`, {
          app: 'GitHub',
          item: filt[0].repo?.split('::')[0]
        }))
      } else if (!filt?.length && !!progressItem?.length) {
        if (intervalFunc) {
          clearInterval(intervalFunc)
        }
        setProgressEnd(true)
      }
    }
  }, [repos, users, isLoadingRepos, isLoadingGithubUsers]);

  const debouncedSearch = useCallback(
    debounce((event) => {
      setPage(0)
      fetchData(0, event.toLowerCase())
    }, 1000),
    [],
  );

  const onSearch = useCallback((event) => {
    const val = event.target.value || ''
    setSearchTerm(val);
    debouncedSearch(val)
  }, []);

  const addOrg = () => {
    if (paymentState !== EPaymentRequiredType.NON) {
      setPaymentPoppup((prev) => ({
        ...prev,
        isOpen: true,
      }));
    } else {
      setNotificationPlace({
        isOpen: true
      });
    }
  };

  return (
    <div id="platform-content" className='list-view'>
      {iniFrame() &&
        <BadgesIFrameMode/>
      }
      {!!progressItem?.length && (
        <BackupBadges progressItem={progressItem} platformName={platformName} isComplete={progressEnd}
                      onClose={() => restartBadge()}/>
      )}
      {(
        addingItemsData.totalItems > 0 && addingItemsData.addedItems > 0 &&
        <BadgesAddingItems platformName={platformName} addingItemsData={addingItemsData}
                           onClose={() => setAddingItemsData({addedItems: 0, totalItems: 0})}/>
      )}
      {trialState === 'active' && trialDays && (
        <BadgesTrial2 trialDays={trialDays} platformName={platformName}/>
      )}
      {!isLoadingRepos && trialState === 'expired' && (!subInfo || subInfo?.status !== 'active') && (
        <BadgesTrial2 platformName={platformName} isExpired={true}
                      color={!!repos?.filter(i => i.lastBackup !== '-').length ? 'error' : 'error_2'}
                      isSub={!!subInfo}/>
      )}
      {appWasDeleted && (
        <BadgesAppWasDeleted platformName={platformName}/>
      )}
      {subInfo && subInfo?.isCancel && subInfo?.finishDateInMillisecond && ((subInfo?.finishDateInMillisecond - Date.now()) / (24 * 60 * 60 * 1000)) < 1 &&
        <BadgesSubscription platformName={platformName}/>}

      <WhiteCard>
        <div className="header-block">
          <Typography variant="h2" gutterBottom display="inline" mr={6} mb={0}>
            <img src={platform.smallLogo} alt={platform.title} loading="lazy" className="platform-logo"/>
            {translate('forms.github_add_edit_repo.your_items', {platform: platform?.title, itemName: ''})}
          </Typography>
          <div className="user-name">
            {!!users?.length && users.some(user => user.isConfirmed) && (
              <Select
                value={userId}
                className='github-org-select'
                MenuProps={{id: 'sub-github-select'}}
              >
                {users?.map(user => (
                  <MenuItem
                    key={user.id}
                    value={user.id}
                    onClick={() => {
                      setIsLoading(true)
                      restartBadge()
                      clearState()
                      dispatch(fetchItemsForGithubUser({
                        page: page,
                        perPage: 10,
                        placeId: user.id as number,
                        filter: filterValue
                      })).unwrap().finally(() => setIsLoading(false))
                      localStorage['githubUser'] = user.id
                      setUserId(user.id as number)
                    }}
                  >
                    @{user.username}
                    {user.userType === EGithubUserTypes.ORGANIZATION ? ' (Organization)' : ': Repositories'}
                  </MenuItem>
                ))}
                {users?.find(i => i.userType === EGithubUserTypes.USER)?.username &&
                  <MenuItem
                    key='0-gist'
                    value={'0'}
                    onClick={() => {
                      setIsLoading(true)
                      restartBadge()
                      clearState()
                      setSearchTerm('')
                      dispatch(fetchGistForGithubUser({
                        page: page,
                        perPage: 10, filter: filterValue
                      })).unwrap().finally(() => setIsLoading(false))
                      localStorage['githubUser'] = 0
                      setUserId(0)
                    }}
                  >
                    @{users?.find(i => i.userType === EGithubUserTypes.USER)?.username}: Gists
                  </MenuItem>
                }
                {!!users && !!users.filter(i => i.isConfirmed).length && (hasUserApp || !!users.filter(i => i.githubapp).length) && (
                  <MenuItem className="add-org-button">
                    <CustomTooltip
                      title={(platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}
                    >
                      <Button fullWidth variant="contained" color="primary" disabled={!isAdminRole}
                              onClick={() => (platformInfo.subPlan?.name === 'Pro' || platformInfo.subPlan?.name === 'Trial') ? addOrg() : updateSubscriptionPlanModal({
                                translate,
                                platformName,
                                navigate
                              })}>
                        <AddSquare size={16} style={{ marginRight: '4px', marginTop: '-2px' }}/>
                        {translate('forms.github_add_edit_repo.add_place')}
                      </Button>
                    </CustomTooltip>
                  </MenuItem>
                )}
              </Select>
            )}
            {!isUserSub && platformInfo && !!platformInfo.users?.length &&
              platformInfo.users.findIndex(i => i.id === userId && i.userType === EGithubUserTypes.ORGANIZATION) !== -1 && (
                <CustomTooltip title={translate('notifications.github_org_info.tooltip')}>
                  <span onClick={() => {
                    setPendingAddReposAfterOrgInfo(false);
                    setIsAppJustAdded(true);
                  }}>
                    <Info className="sliders-icon"/>
                  </span>
                </CustomTooltip>
              )}
          </div>

          <div className="search-block">
            <div className="action-button">
              {(trialState === 'active' || subInfo || !!platformInfo?.users?.length) && !isLoadingGithubUsers && hasUserApp && (
                <CustomTooltip
                  title={(platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}
                >
                  <Button variant="outlined" color="primary"
                          disabled={platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial' || paymentState !== EPaymentRequiredType.NON}
                          onClick={() => setShowSettings2(true)}>
                    <ExtStoreIco className='feather-icon-in-button'/>
                    External storage
                  </Button>
                </CustomTooltip>
              )}
              {!!repos?.length && ((userCanBackup || isTestUser) ? (
                <CustomTooltip
                  title={(platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}>
                  <Button
                    ml={2} variant="contained"
                    color="secondary"
                    onClick={() => (platformInfo.subPlan?.name === 'Pro' || platformInfo.subPlan?.name === 'Trial') ? backupAllNow() : updateSubscriptionPlanModal({
                      translate,
                      platformName,
                      navigate
                    })}
                    disabled={(platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                      (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) || appWasDeleted ||
                      paymentState !== EPaymentRequiredType.NON || !isAdminRole}
                  >
                    <Play size={16} style={{marginRight: '4px'}}/>
                    {translate('views.platform_pages.backupNow')}
                  </Button>
                </CustomTooltip>
              ) : (
                <CustomTimerTooltip setUserCanBackup={() => setUserCanBackup(true)}>
                  <Button
                    ml={2} variant="contained"
                    color="secondary" onClick={backupAllNow}
                    disabled={true}
                  >
                    <Play size={16} style={{marginRight: '4px'}}/>
                    {translate('views.platform_pages.backupNow')}
                  </Button>
                </CustomTimerTooltip>
              ))}
              {!!userId && !!platformInfo.users?.length && !!users?.filter(i => i.isConfirmed).length && (
                <Button
                  ml={2} variant="contained" color="primary" onClick={addRepos}
                  disabled={!isAdminRole || appWasDeleted || isUserSub && subInfo?.status !== 'active' && trialState !== 'active'}
                >
                  <Plus className="feather-icon-in-button"/>
                  {translate('forms.github_add_edit_repo.add_repo_num', {num: ''})}
                </Button>
              )}
              {!userId && !!platformInfo.users?.length && !!users?.filter(i => i.isConfirmed).length && (
                <Button ml={2} variant="contained" color="primary" onClick={addGist}
                        disabled={!isAdminRole || appWasDeleted || isUserSub && subInfo?.status !== 'active' && trialState !== 'active'}
                >
                  <Plus className="feather-icon-in-button"/>
                  {translate('forms.github_add_edit_repo.add_gist_num', {num: ''})}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="header-block header-block-sub">
          <div className="search-block with-select">
            {(!!repos?.length || !!searchTerm || !!filterValue) && (
              <Select
                labelId="grouped-select-label"
                id="grouped-select"
                value={filterValue}
                onChange={(e) => {
                  setFilterValue(e.target.value as string)
                  fetchData(undefined, undefined, e.target.value as string)
                }}
                displayEmpty
                MenuProps={{id: 'filter-select'}}
              >
                <MenuItem value="" className='base-select' disabled>Select filter</MenuItem>
                <MenuItem value="isDailyBackedUp=true">Daily backup: ON</MenuItem>
                <MenuItem value="isDailyBackedUp=false">Daily backup: OFF</MenuItem>
                {/*<MenuItem value="status=backuped">Status: Backuped</MenuItem>*/}
                {/*<MenuItem value="status=deleted">Status: Deleted</MenuItem>*/}
                <Button
                  variant='contained' color='primary' mt={2} fullWidth
                  onClick={() => {
                    setFilterValue('')
                    fetchData(undefined, undefined, '')
                  }}
                >Clear</Button>
              </Select>
            )}
            {(!!repos?.length || !!searchTerm) && (
              <SearchInput
                className="search-field"
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(event) => {
                  onSearch(event)
                }}
                startAdornment={
                  <InputAdornment position="start" className="search-icon">
                    <SearchNormal color='#3B82F6'/>
                  </InputAdornment>
                }
              />
            )}
          </div>
        </div>

        <Box sx={{width: '100%'}}>
          {isLoadingGithubUsers || isLoadingRepos || userLoading || isLoading ?
            <div className="spinner-wrapper">
              <CircularProgress/>
            </div> :
            <div className="data-block">
              {validationBlock()}
              {!validationBlock() &&
                <>
                  <div className="repo-wrapper">
                    <div className="repos-list">
                      <PlatformItemsList
                        items={platformInfo.filteredItems || []}
                        updItems={() => !!userId ? dispatch(fetchItemsForGithubUser({
                          page: page,
                          perPage: 10,
                          placeId: userId, filter: filterValue
                        })).unwrap() : dispatch(fetchGistForGithubUser({
                          page: page,
                          perPage: 10,
                          filter: filterValue
                        })).unwrap()}
                        platformName={platformName}
                        subPlatform={!!userId ? '' : 'gist'}
                        onDelete={handleDeleteRepo}
                        onBulkDelete={handleDeleteBulkBoard}
                        // onBackup={(id, name, type) => {
                        //   return (
                        //     type ?
                        //       dispatch(backupGithubItem(name)).unwrap() :
                        //       dispatch(backupGithubGist(id)).unwrap()
                        //   )
                        // }}
                        setSelectAll={(isSelect) => {
                          if (!isSelect) {
                            setSelectList([])
                            setSelectAll(isSelect)
                          } else {
                            const listToSelect = platformInfo.filteredItems?.filter(item => !item.actionProcess && item.status !== "initialized").map(item => item.id) || []
                            setSelectList(listToSelect)
                            if (listToSelect.length === platformInfo.filteredItems?.length) {
                              setSelectAll(isSelect)
                            }
                          }
                        }}
                        selectAll={selectAll}
                        setSelectList={(name) => (
                          setSelectList(prevState => {
                            const indexToRemove = prevState.indexOf(name);
                            if (indexToRemove !== -1) {
                              prevState.splice(indexToRemove, 1);
                            } else {
                              prevState.push(name)
                            }
                            if (!!prevState?.length && !!platformInfo.filteredItems?.length &&
                              ((prevState.length === platformInfo.filteredItems.length && !selectAll) ||
                                (prevState.length !== platformInfo.filteredItems.length && selectAll))) {
                              setSelectAll(prevState.length === total)
                            }
                            if (!prevState.length) {
                              setSelectAll(false)
                            }
                            return [...prevState]
                          })
                        )}
                        selectList={selectList}
                        subPlan={platformInfo.subPlan}
                        trialState={trialState}
                        appWasDeleted={appWasDeleted}
                        total={total}
                        page={page}
                        setPage={(p) => {
                          setPage(p)
                          clearState(true)
                          fetchData(p, searchTerm)
                        }}
                        isLoading={isLoading}
                        isAdmin={isAdminRole}
                      />
                    </div>
                  </div>
                </>
              }
              <GithubRepoAdd
                availableItemsLeft={getRemainingItems()}
                updItems={() => {
                  dispatch(fetchItemsForGithubUser({
                    page: page,
                    perPage: 10,
                    placeId: userId,
                    filter: filterValue
                  })).unwrap()
                  restartBadge()
                }}
                isOpen={notification.isOpen}
                user={users ? users.filter(i => i.id === userId)[0] : {}}
                closeNotification={closeNotification}
                platformName={platformName}
                paymentType={paymentState}
                subPlan={platformInfo.subPlan}
                isAppInstall={hasUserApp || !!users?.filter(i => i.githubapp).length}
              />
              <GithubAddOrg
                isOpen={notificationPlace.isOpen}
                user={users ? users.filter(i => i.id === userId)[0] : {}}
                closeNotification={closeNotification}
                openOrgInfo={() => {
                  setPendingAddReposAfterOrgInfo(false);
                  setIsAppJustAdded(true);
                }}
              />
              <GithubGistAdd
                availableItemsLeft={getRemainingItems()}
                updItems={() => {
                  dispatch(fetchGistForGithubUser({page: page, perPage: 10, filter: filterValue})).unwrap()
                  restartBadge()
                }}
                isOpen={notificationSec.isOpen}
                user={users ? users.filter(i => i.userType === EGithubUserTypes.USER)[0] : {}}
                closeNotification={closeNotification}
                platformName={platformName}
                paymentType={paymentState}
                subPlan={platformInfo.subPlan}
                isAppInstall={hasUserApp || !!users?.filter(i => i.githubapp).length}
              />
              <PaymentRequiredPoppup
                isOpen={paymentPoppup.isOpen}
                closeNotification={closeTrialPoppup}
                type={paymentState}
                platformName={paymentPoppup.platformName}
              />
              <Modal
                isOpen={showSettings}
                closeNotification={() => setShowSettings(false)}
                title='Edit settings'
                child={<GithubUserAddEdit
                  user={users ? users.filter(i => i.id === userId)[0] : {}}
                  subPlan={platformInfo.subPlan}
                  page={page}
                  onClose={() => setShowSettings(false)}
                />}
              />
              <Modal
                isOpen={showSettings2}
                className='ext-storage-modal'
                closeNotification={() => setShowSettings2(false)}
                child={<ExternalStorage
                  platformModal='github'
                  closeModal={() => setShowSettings2(false)}
                />}
              />
              <DeleteItemDialog
                isOpen={!!deleteDate?.name?.length || !!deleteBulkDate.length}
                closeNotification={() => {
                  clearState(true)
                }}
                platformName={platformName}
                isBulk={!!deleteBulkDate.length}
                bulkData={deleteBulkDate}
                singleData={deleteDate}
                paymentState={paymentState}
                items={platformInfo.filteredItems || []}
                name={deleteDate?.name || ''}
                updItems={() => !!userId ? dispatch(fetchItemsForGithubUser({
                  page: page,
                  perPage: 10,
                  placeId: userId, filter: filterValue
                })).unwrap() : dispatch(fetchGistForGithubUser({
                  page: page,
                  perPage: 10,
                  filter: filterValue
                })).unwrap()}
                onConfirm={() => {
                  deleteBulkDate.length > 1 ?
                    onConfirmDeleteRepo(deleteDate?.id, deleteDate?.name, true) :
                    onConfirmDeleteRepo(deleteBulkDate[0])
                }}
              />
            </div>
          }
        </Box>
        <GithubOrgAccInfoDialog
          isOpen={isAppJustAdded}
          closeNotification={handleOrgInfoClose}
        />
      </WhiteCard>
    </div>
  );
};

export default withRedirectAfterLogout()(GithubPlatformSettings);
